import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  Stack,
  Pagination,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableItem from "../../../components/TableItem";
import InputField from "../../../FormComponents/InputField";
import Alart from "../../../components/Alart";
import { getSubOrderPersonByPaging } from "../../../services/SubOrderPerson";
import ItemSelectingPopup from "../../../components/ItemSelectingPopup";

// Icons
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddSubOrderPerson from "./AddSubOrderPerson";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
  backgroundColor: "background.default",
}));

const HeaderCard = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: alpha(theme.palette.background.default, 0.6),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: "none",
}));

const TableContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  overflow: "hidden",
  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.05)",
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginTop: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius * 1.5,
  },
}));

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  minWidth: "100%",
  justifyContent: "flex-start",
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.2),
    borderColor: theme.palette.primary.main,
  },
}));

let _subOrderPersonListState = null;

function SubOrderPerson() {
  const theme = useTheme();
  const history = useHistory();

  // State variables
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [openInputItemTable, setOpenInputItemTable] = useState(false);
  const [selectedInputItems, setSelectedInputItems] = useState([]);
  const [openOutputItemTable, setOpenOutputItemTable] = useState(false);
  const [selectedOutputItems, setSelectedOutputItems] = useState([]);
  const [itemRows, setItemRows] = useState([]);

  // Disable sub order person warning state
  const [openDisableWarning, setOpenDisableWarning] = useState(false);
  const [msgDisableWarning, setMsgDisableWarning] = useState({
    title: "Warning",
    content: "Are you sure you want to disable this sub order person?",
  });
  const [disableItemId, setDisableItemId] = useState(null);

  // Filter state
  const [filter, setFilter] = useState({
    _name: "",
    _email: "",
    _status: "",
    _address: "",
    _outputImportIds: [],
    _inputImportIds: [],
    _phone: "",
  });

  // Table columns definition
  const columns = [
    { id: "name", label: "Name", minWidth: 150 },
    { id: "phone", label: "Phone", minWidth: 120 },
    { id: "email", label: "Email", minWidth: 180 },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      format: (value) => (
        <Box
          sx={{
            display: "inline-block",
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            bgcolor:
              value === "Active"
                ? alpha(theme.palette.success.main, 0.1)
                : alpha(theme.palette.error.main, 0.1),
            color: value === "Active" ? "success.main" : "error.main",
            fontWeight: "medium",
          }}
        >
          {value}
        </Box>
      ),
    },
    { id: "action", label: "Actions", minWidth: 150 },
  ];

  // Init data fetch
  useEffect(() => {
    if (_subOrderPersonListState) {
      const { page: savedPage, filter: savedFilter } = _subOrderPersonListState;
      _subOrderPersonListState = null;
      setPage(savedPage);
      setFilter(savedFilter);
      fetchData(savedFilter, savedPage);
    } else {
      fetchData(filter, page);
    }
  }, []);

  // Data creation helper
  function createData(name, phone, email, address, status, action, person_id) {
    return { name, phone, email, address, status, action, person_id };
  }

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(filter, value - 1);
  };

  // Main data fetch function
  const fetchData = async (subOrderPersonFilter, pageNo) => {
    setIsLoading(true);
    try {
      const result = await getSubOrderPersonByPaging(
        pageNo,
        rowsPerPage,
        subOrderPersonFilter
      );
      const subPersonSet = result.content;

      // Set pagination info
      setTotalPages(result.totalPages);
      setTotalRows(result.totalElements);

      // Format data for table
      const newSet = [];
      if (subPersonSet && subPersonSet.length > 0) {
        for (let x = 0; x < subPersonSet.length; x++) {
          newSet.push(
            createData(
              subPersonSet[x].personName,
              subPersonSet[x].phone,
              subPersonSet[x].email,
              subPersonSet[x].address,
              subPersonSet[x].status,
              subPersonSet[x].personId
            )
          );
        }
      }

      setRows(newSet);
    } catch (error) {
      console.error("Error fetching Sub Order Person data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Action handler for table rows
  const handleAction = async (event, id) => {
    switch (event) {
      case "view":
        _subOrderPersonListState = { page, filter };
        history.push("/template/sub_order_person_view/" + id);
        break;
      case "edit":
        _subOrderPersonListState = { page, filter };
        history.push("/template/sub_order_person_edit/" + id);
        break;
      case "disable":
        disableProduct(id);
        break;
      default:
        return;
    }
  };

  // Disable product handler
  const disableProduct = async (id) => {
    setDisableItemId(id);
    setOpenDisableWarning(true);
  };

  // Action buttons for disable warning
  function DisableWarningActionButtons() {
    return (
      <>
        <ActionButton
          variant="contained"
          color="error"
          onClick={async () => {
            // await DiactivateItemById(disableItemId);
            setOpenDisableWarning(false);
            await fetchData(filter, page);
          }}
        >
          Disable
        </ActionButton>
        <ActionButton
          variant="outlined"
          onClick={() => {
            setOpenDisableWarning(false);
          }}
        >
          Cancel
        </ActionButton>
      </>
    );
  }

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _personName: "",
      _email: "",
      _status: "",
      _address: "",
      _phone: "",
      _inputImportIds: [],
      _outputImportIds: [],
    });
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Item popup handlers
  const handleOpenInputItemTable = async () => {
    setOpenInputItemTable(true);
  };

  const handleCloseInputItemTable = async () => {
    setOpenInputItemTable(false);
    setFilter({
      ...filter,
      _inputImportIds: selectedInputItems,
    });
  };

  // Item popup handlers
  const handleOpenOutputItemTable = async () => {
    setOpenOutputItemTable(true);
  };

  const handleCloseOutputItemTable = async () => {
    setOpenOutputItemTable(false);
    setFilter({
      ...filter,
      _outputImportIds: selectedOutputItems,
    });
  };

  // If form is open, show Add Sub Order Person component
  if (openForm) {
    return (
      <StyledPaper elevation={3}>
        <AddSubOrderPerson setOpenForm={setOpenForm} />
      </StyledPaper>
    );
  }

  return (
    <>
      <Alart
        open={openDisableWarning}
        message={msgDisableWarning}
        actions={<DisableWarningActionButtons />}
      />

      <StyledPaper elevation={3}>
        {/* Header */}
        <HeaderCard>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <PersonIcon fontSize="large" color="primary" />
            <Box>
              <Typography variant="h5" fontWeight="600" color="primary.main">
                Sub Order Person Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage sub order person accounts and information
              </Typography>
            </Box>
          </Box>
          <RoleBasedRender
            allowedRoles={["SUB_ORDER_PERSON_CREATE", "ROLE_ADMIN"]}
          >
            <ActionButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenForm(true)}
            >
              Add Sub Order Person
            </ActionButton>
          </RoleBasedRender>
        </HeaderCard>

        <Divider sx={{ mb: 3 }} />

        {/* Search and filter controls */}
        <Box
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <StyledTextField
            placeholder="Search by person name..."
            variant="outlined"
            size="small"
            sx={{ width: "40%" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              handleFilterChange("_name", e.target.value);
              if (e.target.value.length > 2 || e.target.value.length === 0) {
                fetchData({ ...filter, _name: e.target.value }, page);
              }
            }}
          />
          <Stack direction="row" spacing={2}>
            <Tooltip title="Toggle Filters">
              <ActionButton
                variant="outlined"
                color="primary"
                startIcon={<FilterListIcon />}
                onClick={toggleFilters}
              >
                Filters
              </ActionButton>
            </Tooltip>
            <Chip
              label={`${totalRows} Persons`}
              variant="outlined"
              color="primary"
            />
          </Stack>
        </Box>

        {/* Expanded filters */}
        {showFilters && (
          <FilterContainer>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                Advanced Filters
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <InputField
                    name="_personName"
                    value={filter._name}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Person Name"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <InputField
                    name="_email"
                    value={filter._email}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Email"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <InputField
                    name="_status"
                    value={filter._status}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Status"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <InputField
                    name="_address"
                    value={filter._address}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Address"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <SelectButton
                    startIcon={<InventoryIcon />}
                    onClick={handleOpenInputItemTable}
                    endIcon={
                      selectedInputItems.length > 0 ? (
                        <Chip size="small" label={selectedInputItems.length} />
                      ) : null
                    }
                  >
                    {selectedInputItems.length > 0
                      ? "Input Items Selected"
                      : "Select Input Items"}
                  </SelectButton>
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <SelectButton
                    startIcon={<InventoryIcon />}
                    onClick={handleOpenOutputItemTable}
                    endIcon={
                      selectedOutputItems.length > 0 ? (
                        <Chip size="small" label={selectedOutputItems.length} />
                      ) : null
                    }
                  >
                    {selectedOutputItems.length > 0
                      ? "Output Items Selected"
                      : "Select Output Items"}
                  </SelectButton>
                </Grid>
              </Grid>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Reset Filters">
                    <ActionButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<RestartAltIcon />}
                      onClick={() => {
                        resetFilters();
                        fetchData({}, page);
                      }}
                    >
                      Reset
                    </ActionButton>
                  </Tooltip>
                  <Tooltip title="Apply Filters">
                    <ActionButton
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={() => fetchData(filter, page)}
                    >
                      Search
                    </ActionButton>
                  </Tooltip>
                </Stack>
              </Box>
            </CardContent>
          </FilterContainer>
        )}

        {/* Sub Order Person Table */}
        <TableContainer elevation={1}>
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <CircularProgress />
                <Typography color="text.secondary">
                  Loading Sub Order Persons...
                </Typography>
              </Box>
            ) : rows.length > 0 ? (
              <TableItem
                columns={columns}
                rows={rows}
                page={page}
                setPage={setPage}
                tablePagin={false}
                totalPages={totalPages}
                handleAction={handleAction}
                showActions={{
                  view: "Person view",
                  edit: "Person edit",
                  disable: "Person Diactvate",
                }}
                actionRoles={{
                  view: ["SUB_ORDER_PERSON_VIEW", "ROLE_ADMIN"],
                  edit: ["SUB_ORDER_PERSON_EDIT", "ROLE_ADMIN"],
                  disable: ["SUB_ORDER_PERSON_DELETE", "ROLE_ADMIN"],
                }}
                tableProps={{
                  sx: {
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: alpha("#000", 0.04),
                    },
                  },
                }}
              />
            ) : (
              <Box
                sx={{
                  py: 6,
                  textAlign: "center",
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                }}
              >
                <PersonIcon
                  sx={{
                    fontSize: 60,
                    color: alpha(theme.palette.text.secondary, 0.2),
                    mb: 1,
                  }}
                />
                <Typography variant="h6" color="text.secondary">
                  No sub order persons found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Try adjusting your search filters or add a new sub order
                  person
                </Typography>
              </Box>
            )}
          </CardContent>
        </TableContainer>

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <PaginationContainer>
            <Pagination
              count={totalPages}
              page={page + 1}
              variant="outlined"
              shape="rounded"
              color="primary"
              onChange={handleChange}
              size="medium"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: theme.shape.borderRadius,
                },
              }}
            />
          </PaginationContainer>
        )}

        {openInputItemTable && (
          <ItemSelectingPopup
            setOpenTable={setOpenInputItemTable}
            openTable={openInputItemTable}
            handleCloseTable={handleCloseInputItemTable}
            setSelectedItems={setSelectedInputItems}
            selectedItems={selectedInputItems}
            setRows={setItemRows}
            rows={itemRows}
          />
        )}
        {openOutputItemTable && (
          <ItemSelectingPopup
            setOpenTable={setOpenOutputItemTable}
            openTable={openOutputItemTable}
            handleCloseTable={handleCloseOutputItemTable}
            setSelectedItems={setSelectedOutputItems}
            selectedItems={selectedOutputItems}
            setRows={setItemRows}
            rows={itemRows}
          />
        )}
      </StyledPaper>
    </>
  );
}

export default SubOrderPerson;
